import {securedApiRequest} from "./SecuredApi";

/**
 * Handles user registration by sending a POST request to the registration endpoint.
 *
 * @async
 * @function
 * @param {Object} userData - The user data to be sent for registration, typically containing personal and account details.
 * @returns {Promise<Object>} A promise that resolves to the response from the server upon successful registration.
 */
export const registerUser = async (userData) => {
    return securedApiRequest("/registration/", "POST", userData, false);
};

/**
 * Verifies an authentication code by sending it to a secure API endpoint.
 *
 * This function sends a POST request to the "/auth-verify/" endpoint with the provided activation code
 * and returns the result of the secured API request.
 *
 * @async
 * @function verifyAuthCode
 * @param {string} activationCode - The activation code to be verified.
 * @returns {Promise<*>} The response from the secured API request.
 */
export const verifyAuthCode = async (activationCode) => {
    return securedApiRequest("/auth-verify/", "POST", { activationCode }, false);
};

/**
 * Sends a request to resend the activation code to the specified email address.
 *
 * This asynchronous function uses a secured API request to trigger the
 * re-sending of an activation code. Typically used in scenarios where
 * a user did not receive or has lost their initial activation email.
 *
 * @param {string} email - The email address of the user to which the activation code should be resent.
 * @returns {Promise<Object>} A promise that resolves to the response of the API request.
 */
export const resendActivationCode = async (email) => {
    return securedApiRequest("/resend-code/", "POST", { email }, false);
};


/**
 * Authenticates a user against the system using the provided username and password credentials.
 *
 * @async
 * @function loginUser
 * @param {string} username - The username of the user attempting to log in.
 * @param {string} password - The password of the user attempting to log in.
 * @returns {Promise<Object>} A promise that resolves with the server's response, typically containing session or authentication details.
 */
export const loginUser = async (username, password) => {
    return securedApiRequest("/login/", "POST", { username, password }, false);
};


/**
 * Fetch the current user's details once we have a token
 */
export const getUserDetails = async (token) => {
    return securedApiRequest("/get-user-details/", "GET", null, true);
};


/**
 * Refreshes the access token using the HttpOnly refresh cookie.
 *
 * @returns {Promise<string|null>} The new access token, or null if refresh fails.
 */
export const refreshAccessToken = async () => {
    try {
        const response = await fetch("/api/refresh-token/", {
            method: "POST",
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error("Failed to refresh token");
        }

        const data = await response.json();
        return data.access_token;
    } catch (error) {
        console.error("Token refresh failed:", error);
        return null;
    }
};


/**
 * Logs out a user by invalidating their session on the server.
 * Relies on Django's session logout and CSRF protection.
 * @async
 * @function logoutUser
 * @returns {Promise<{message: string}>} A promise resolving to the server response.
 */
export const logoutUser = async () => {
    return securedApiRequest("/logout/", "POST", null, true);
};

/**
 * Resets the password for a specific user using their unique identifier and a token.
 *
 * This function sends a secure API request to update the user's password.
 * The provided `uid` and `token` are used to authenticate the request.
 *
 * @param {string} uid - The unique identifier of the user whose password is being reset.
 * @param {string} token - The token used for authentication and validation of the password reset request.
 * @param {string} newPassword - The new password to set for the user account.
 * @returns {Promise<Object>} A promise that resolves to the server response after the password reset request, or throws an error if the request fails.
 */
export const resetPassword = async (uid, token, newPassword) => {
    return securedApiRequest(`/reset-password/${uid}/${token}/`, "POST", { password: newPassword }, false);
};


/**
 * Sends a request to initiate the password reset process for a given email.
 *
 * This function makes an asynchronous POST request to the "/forgot-password/" endpoint,
 * providing the user's email address in the request body. Typically, the endpoint responds
 * by sending a password reset link or further instructions to the specified email address.
 *
 * @param {string} email - The email address of the user who wants to reset their password.
 * @returns {Promise<any>} A promise that resolves to the response from the secured API request.
 */
export const forgotPassword = async (email) => {
    return securedApiRequest("/forgot-password/", "POST", { username: email }, false);
};